import * as core from 'cw-ui-core';
import { GET, POST, PATCH, PUT, DELETE } from 'src/services/ApiService';
import Routes from 'src/services/Routes';
import { put, take } from 'redux-saga/effects';
import parse from 'parse-link-header';
import { getUserContactInfoFromClaims } from 'src/services/ContactClaimsService';
import Locale from 'src/Locale';
import { isRoleFilterViewActive } from 'src/services/userRoleFilterUtils';
import { USERS_LEGACY_HANDOFF_HIDE_SEARCH_TYPE_KEY } from 'src/services/userSearchValidation';

export function parseUserListResponse(response) {
    if (response == null) {
        return [];
    }

    const plainResponse = typeof response.toJS === 'function' ? response.toJS() : response;
    const users = Array.isArray(plainResponse) ? plainResponse : [plainResponse];
    return users
        .filter(user => user != null)
        .map(user => (typeof user.toJS === 'function' ? user.toJS() : user));
}

function getUserRecordId(user) {
    if (!user) {
        return null;
    }
    if (typeof user.get === 'function') {
        return user.get('id') || user.get('Id') || user.get('userId') || user.get('UserId') || user.get('ID');
    }
    return user.id || user.Id || user.userId || user.UserId || user.ID;
}

export async function fetchUsersForPartnerRole(partnerId, roleId) {
    const result = await GET(`/user/allusers/${partnerId}/${roleId}`);
    const hasUsersPayload = result?.response != null;
    if (!result.isSuccess && !hasUsersPayload) {
        return {
            users: null,
            error: result.error,
            errorMessages: result.errorMessages,
        };
    }

    return {
        users: parseUserListResponse(result.response),
        error: null,
        errorMessages: null,
    };
}

/**
 * Loads users for one or more identity role IDs.
 * OR logic across roleIds; de-duplicates by user id.
 */
export async function loadUsersForPartnerRoles(partnerId, roleIds) {
    const uniqueRoleIds = [...new Set((roleIds || []).filter(Boolean))];
    if (!partnerId || !uniqueRoleIds.length) {
        return [];
    }

    const results = await Promise.all(uniqueRoleIds.map(roleId => fetchUsersForPartnerRole(partnerId, roleId)));

    const usersById = new Map();
    for (const result of results) {
        if (result.users === null) {
            const error = `Failed to load users for identity role filter. ${result.error}`;
            core.CwLog.error(error);
            core.Store().dispatch(core.setErrorScreenMessage(error, false, result.errorMessages));
            return null;
        }

        result.users.forEach(user => {
            const userId = getUserRecordId(user);
            if (userId) {
                usersById.set(userId, user);
            }
        });
    }

    return Array.from(usersById.values());
}

// Users array is stored at [Routes.USERS.id], so loading must use a sibling key.
export const USERS_LIST_IS_LOADING = 'usersListIsLoading';

let usersListLoadGeneration = 0;

const startUsersListLoading = () => {
    usersListLoadGeneration += 1;
    core.Store().dispatch(core.setScreenData(USERS_LIST_IS_LOADING, true));
    return usersListLoadGeneration;
};

const isCurrentUsersListLoad = loadGeneration => loadGeneration === usersListLoadGeneration;

const finishUsersListLoading = loadGeneration => {
    if (isCurrentUsersListLoad(loadGeneration)) {
        core.Store().dispatch(core.setScreenData(USERS_LIST_IS_LOADING, false));
    }
};

/** Invalidates in-flight loadUserList requests (e.g. user changed search type mid-load). */
export const cancelActiveUsersListLoad = () => {
    usersListLoadGeneration += 1;
    core.Store().dispatch(core.setScreenData(USERS_LIST_IS_LOADING, false));
};

export async function loadAllUsers() {
    const state = core.Store().getState();

    let result = undefined;
    const partnerId = core.getScreenCustom(state.screen, 'partnerId') || '';
    result = await GET(`/user/allusers/${partnerId}`);
    
    if (!result.isSuccess) {
        const error = 'Failed to load users list. ' + result.error;
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false, result.errorMessages));
        return;
    }
    let users = Array.isArray(result.response) ? result.response : [result.response];

    core.Store().dispatch(core.setScreenData([Routes.USERS.id], users));
}

const getApiErrorDetail = result => {
    if (typeof result?.response === 'string' && result.response.trim()) {
        return result.response.trim();
    }
    if (result?.errorMessages?.length) {
        return result.errorMessages.join(', ');
    }
    return result?.error;
};

export async function loadUserList() {
    const state = core.Store().getState();
    if (isRoleFilterViewActive(state)) {
        return true;
    }

    const page = core.getModuleCache(state.module, [Routes.USERS.id, 'pager', 'currentPage']) || 1;
    const PAGE_SIZE = core.getModuleCache(state.module, [Routes.USERS.id, 'pager', 'pageSize']) || 1;

    let result = undefined;
    const search = core.getModuleCache(state.module, [Routes.USERS.id, 'search']) || '';
    const searchType = core.getModuleCache(state.module, [Routes.USERS.id, 'searchType']) || '';
    const searchParam = search.trim();
    const screenPartnerId = core.getScreenCustom(state.screen, 'partnerId') || '';
    const isHomeHandoffResultsView = !!core.getModuleCache(
        state.module,
        USERS_LEGACY_HANDOFF_HIDE_SEARCH_TYPE_KEY
    );
    // Typed search applies only on global Users (no partner context). Partner flows keep legacy behavior.
    // Home → Users handoff keeps legacy inference (no searchType) until the user runs a typed search.
    const isGlobalTypedSearch = Boolean(searchType) && !screenPartnerId && !isHomeHandoffResultsView;
    const partnerId = isGlobalTypedSearch ? '' : screenPartnerId;
    if (!isGlobalTypedSearch && searchParam.length > 0 && searchParam.length < 3) {
        const error = Locale.user_search_min_length.defaultMessage;
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false));
        core.Store().dispatch(core.setModuleCache([Routes.USERS.id, 'search'], null)); // Clear search after complete
        return false;
    }

    // Global users page must not fetch until a search value is provided.
    if (isGlobalTypedSearch && !searchParam) {
        return true;
    }

    const loadGeneration = startUsersListLoading();
    try {
        let url = `/user/users?partnerId=${partnerId}&search=${encodeURIComponent(searchParam)}&page=${page}&pageSize=${PAGE_SIZE}`;
        if (isGlobalTypedSearch) {
            url += `&searchType=${encodeURIComponent(searchType)}`;
        }
        result = await GET(url);
        if (!isCurrentUsersListLoad(loadGeneration)) {
            return false;
        }
        if (!result.isSuccess) {
            const detail = getApiErrorDetail(result) || 'Unknown error';
            const error = `Failed to load users list. ${detail}`;
            core.CwLog.error(error);
            if (isGlobalTypedSearch) {
                core.Store().dispatch(core.setModuleCache([Routes.USERS.id, 'searchError'], detail));
                core.Store().dispatch(core.removeScreenData([Routes.USERS.id]));
                core.Store().dispatch(core.setModuleCache([Routes.USERS.id, 'pager', 'totalPages'], 1));
            } else if (isHomeHandoffResultsView) {
                core.Store().dispatch(core.setModuleCache([Routes.USERS.id, 'searchError'], detail));
            } else {
                core.Store().dispatch(core.setErrorScreenMessage(error, false, result.errorMessages));
                core.Store().dispatch(core.setModuleCache([Routes.USERS.id, 'search'], null));
            }
            return false;
        }
        let users = [];
        if (Array.isArray(result.response)) {
            users = result.response;
        } else if (result.response) {
            users = [result.response];
        }
        // ToDo: Once query conditional support added to STS endpoints, this check will be handled by the tenant and query condition
        if (search && screenPartnerId && !isGlobalTypedSearch) {
            // Hit search url, users (if any) should include only one entry
            if (users[0] && users[0].partnerId !== screenPartnerId) {
                const error = `Failed to find user ${search} on this partner`;
                core.CwLog.error(error);
                core.Store().dispatch(core.setErrorScreenMessage(error, false));
                core.Store().dispatch(core.setModuleCache([Routes.USERS.id, 'search'], null));
                return false;
            }
        }
        // If search term, users list should only be one user total
        let numberOfPages = page;
        if (result.headers) {
            const parsed = parse(result.headers.get('link'));
            if (parsed) {
                if (parsed['last'] && parsed['last']['page']) {
                    numberOfPages = parseInt(parsed['last']['page']);
                }
            }
        }
        if (!isCurrentUsersListLoad(loadGeneration)) {
            return false;
        }
        const currentState = core.Store().getState();
        if (isRoleFilterViewActive(currentState)) {
            return true;
        }
        core.Store().dispatch(core.setModuleCache([Routes.USERS.id, 'pager', 'totalPages'], numberOfPages || page));
        core.Store().dispatch(core.setScreenData([Routes.USERS.id], users));
        return true;
    } finally {
        finishUsersListLoading(loadGeneration);
    }
}

export async function loadUser(partnerId, userId) {
    if (!partnerId || !userId) {
        const error = `Invalid parameters to load user query`;
        core.CwLog.error(error);
        core.Store.dispatch(core.setErrorScreenMessage(error, false));
        return;
    }
    let result = undefined;

    result = await GET(`/user/${partnerId}/users/${userId}`);

    if (!result.isSuccess) {
        const error = `Failed to load user. ${result.error}`;
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false, result.errorMessages));
        return;
    }

    let user = result.response;
    user.contactInfo = getUserContactInfoFromClaims(user.additionalClaims);

    const [roles, clients, mfaMethods] = await Promise.all([
        loadUserRoles(partnerId, userId),
        getUserClients(partnerId, userId),
        user.loginScheme != null
            ? Promise.resolve({ validProviders: ['ExternalAuth'] })
            : loadUserMFAMethods(partnerId, userId),
    ]);

    user.roles = roles;
    user.clients = clients;
    user.mfaMethods = mfaMethods;

    core.Store().dispatch(core.setScreenCustom(['staleContact'], { contactId: user.contactInfo?.contactId, isStaleContact: false }));
    core.Store().dispatch(core.setScreenData([Routes.USER.id], user));
    if (user.contactInfo?.contactId) {
        const sfcontact = await POST(`/user/sfcontact/${user.contactInfo?.contactId}`, null);
        if (!sfcontact.isSuccess) {
            core.Store().dispatch(core.setScreenCustom(['staleContact'], { contactId: user.contactInfo?.contactId, isStaleContact: true }));
        }
    }

    return user;
}

export const getStaleContact = async (contactId) => {
    const sfcontact = await POST(`/user/sfcontact/${contactId}`, null);
    if (!sfcontact.isSuccess) {
        core.Store().dispatch(core.setScreenCustom(['staleContact'], { contactId, isStaleContact: true }));
    }
    else {
        core.Store().dispatch(core.setScreenCustom(['staleContact'], { contactId, isStaleContact: false }));
    }
    return sfcontact;
};

async function getUserRoles(partnerId, userId, options = {}) {
    const showPageError = options.showPageError !== false;
    if (!partnerId || !userId) {
        const error = `Invalid parameters to load user query`;
        core.CwLog.error(error);
        if (showPageError) {
            core.Store.dispatch(core.setErrorScreenMessage(error, false));
        }
        return;
    }
    let result = undefined;

    result = await GET(`/user/${partnerId}/users/${userId}/roles`);

    if (!result.isSuccess) {
        const error = `Failed to load user roles. ${result.error}`;
        core.CwLog.error(error);
        if (showPageError) {
            core.Store().dispatch(core.setErrorScreenMessage(error, false, result.errorMessages));
        }
        return;
    }
    let roles = result.response || [];
    roles.map(role => (role.isAssigned = true));

    return roles;
}

async function getUsersRolesBulk(requests) {
    if (!requests?.length) {
        return null;
    }

    const result = await POST('/user/users/roles', requests);

    if (!result.isSuccess) {
        const error = `Failed to load user roles. ${result.error}`;
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false, result.errorMessages));
        return null;
    }

    const rolesByUserId = result.response?.rolesByUserId || {};
    if (result.response?.roleFetchError) {
        core.Store().dispatch(core.setErrorScreenMessage(result.response.roleFetchError, false));
    }

    Object.values(rolesByUserId).forEach(roles => {
        roles?.forEach(role => {
            role.isAssigned = true;
        });
    });

    return rolesByUserId;
}

async function getUserRolesMapping(partnerId, userId, identityRoleIds, options = {}) {
    const showPageError = options.showPageError !== false;
    if (!partnerId || !userId) {
        const error = `Invalid parameters to load user query`;
        core.CwLog.error(error);
        if (showPageError) {
            core.Store().dispatch(core.setErrorScreenMessage(error, false));
        }
        return;
    }
    let result = undefined;

    result = await GET(`/user/${partnerId}/users/${userId}/roles/${identityRoleIds}`);

    if (!result.isSuccess) {
        const error = `Failed to load user roles. ${result.error}`;
        core.CwLog.error(error);
        if (showPageError) {
            core.Store().dispatch(core.setErrorScreenMessage(error, false, result.errorMessages));
        }
        return;
    }
    let rolesMapping = result.response || [];
    return rolesMapping;
}

export const IDENTITY_ROLES_LOAD_FAILED = 'Unable to load identity roles';

export async function getColleaguePiRoles(partnerId, userId) {
    if (!partnerId || !userId) {
        const error = `Invalid parameters to load colleague query`;
        core.CwLog.error(error);
        return null;
    }

    const identityRoles = await getUserRoles(partnerId, userId, { showPageError: false });
    if (identityRoles === undefined || identityRoles === null) {
        return null;
    }

    if (!identityRoles.length) {
        return [];
    }

    try {
        const identityRoleIds = identityRoles.map(iRole => iRole.id).join(',');
        const rolesMapping = await getUserRolesMapping(partnerId, userId, identityRoleIds, { showPageError: false });
        if (rolesMapping === undefined || rolesMapping === null) {
            return null;
        }

        const mappedPiRoleIds = getMappedProductInsightsIdentityRoleIds(
            rolesMapping,
            identityRoles.map(iRole => iRole.id)
        );
        if (mappedPiRoleIds === null) {
            return null;
        }

        return identityRoles.filter(iRole => mappedPiRoleIds.has(iRole.id)).map(iRole => iRole.name);
    } catch (error) {
        core.CwLog.error(`Failed to load colleague PI role mappings. ${error}`);
        return null;
    }
}

/** Same PI filter as pre-batch: productinsights connectedApp JSON contains the identity role id. */
function getMappedProductInsightsIdentityRoleIds(rolesMapping, candidateRoleIds) {
    let mapping = rolesMapping;
    if (typeof mapping === 'string') {
        try {
            mapping = JSON.parse(mapping);
        } catch {
            return null;
        }
    }

    const productInsightsApp = mapping?.connectedApps?.find(
        app => app?.connectedAppId === 'productinsights'
    );
    const processedCheck = JSON.stringify(productInsightsApp);
    if (!processedCheck) {
        return new Set();
    }

    const mappedRoleIds = new Set();
    (candidateRoleIds || []).forEach(roleId => {
        if (processedCheck.includes(roleId)) {
            mappedRoleIds.add(roleId);
        }
    });

    return mappedRoleIds;
}

async function getUserMFAMethods(partnerId, userId) {
    if (!partnerId || !userId) {
        const error = `Invalid parameters to load user query`;
        core.CwLog.error(error);
        core.Store.dispatch(core.setErrorScreenMessage(error, false));
        return;
    }
    let result = undefined;

    result = await GET(`/user/${partnerId}/users/${userId}/mfaMethods`);

    if (!result.isSuccess) {
        const error = `Failed to load user MFA details. ${result.error}`;
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false, result.errorMessages));
        return;
    }
    let mfaMethods = result.response || [];

    return mfaMethods;
}

export async function disableAuthAppMFA(partnerId, userId) {
    if (!partnerId || !userId) {
        const error = `Invalid parameters to disable MFA`;
        core.CwLog.error(error);
        core.Store.dispatch(core.setErrorScreenMessage(error, false));
        return;
    }

    const validProviders = { "Provider": "Authenticator" };

    let result = undefined;

    core.Store().dispatch(core.toggleMask(true));

    result = await POST(`/user/${partnerId}/users/${userId}/disableMFA`, validProviders);

    if (!result.isSuccess) {
        const error = `Failed to disable user MFA details. ${result.error}`;
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false, result.errorMessages));
        return;
    }

    core.Store().dispatch(core.setSuccessfulScreenMessage('Successfully disabled Auth App MFA.'));
    core.Store().dispatch(core.toggleMask(false));
    return true;
}

async function getUserClients(partnerId, userId) {
    if (!partnerId || !userId) {
        const error = `Invalid parameters to load user clients`;
        core.CwLog.error(error);
        core.Store.dispatch(core.setErrorScreenMessage(error, false));
        return;
    }

    const [result, clientsResult] = await Promise.all([
        GET(`/client/tenants/${partnerId}/users/${userId}/clients`),
        GET(`/client/tenants/${partnerId}/clients`),
    ]);

    if (!result.isSuccess) {
        const error = `Failed to load user clients. ${result.error}`;
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false));
        return;
    }

    if (!clientsResult.isSuccess) {
        const error = `Failed to load partner clients. ${clientsResult.error}`;
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false));
        return;
    }

    const clientsMap = new Map(clientsResult.response.map(x => [x.clientId, x]));

    let clients = result.response || [];
    clients = clients.map(x => clientsMap.get(x)).filter(x => x != null);

    return clients;
}

export async function getPartnerRoles(partnerId) {
    if (!partnerId) {
        const error = `Invalid parameters to load roles query`;
        core.CwLog.error(error);
        core.Store.dispatch(core.setErrorScreenMessage(error, false));
        return;
    }
    let result = undefined;

    result = await GET(`/role/${partnerId}`);

    if (!result.isSuccess) {
        const error = `Failed to load partner roles. ${result.error}`;
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false));
        return;
    }
    let roles = result.response || [];

    return roles;
}

export async function loadPartnerRoles(partnerId, userId = null) {
    let roles = await getPartnerRoles(partnerId);
    const identityRoleIds = roles.map(function (m) {
        return m.id;
    });
    let rolesMapping = await getUserRolesMapping(partnerId, userId, String(identityRoleIds));
    core.Store().dispatch(core.setScreenCustom(['rolesMapping'], rolesMapping));
    core.Store().dispatch(core.setScreenCustom(['partnerRoles'], roles));
}

export async function loadUserRoles(partnerId, userId) {
    let roles = await getUserRoles(partnerId, userId);
    core.Store().dispatch(core.setScreenCustom(['roles'], roles));
}

export async function loadUserMFAMethods(partnerId, userId) {
    let mfaMethods = await getUserMFAMethods(partnerId, userId);
    return mfaMethods;
}

export async function loadUsersHaveClients(users) {
    if (!users) return null;

    Promise.all(users.map(user => getUserClients(user.partnerId, user.id))).then(results => {
        const clientsMap = new Map(results.map((clients, index) => [users[index].id, clients.length > 0]));
        core.Store().dispatch(core.setScreenCustom(['clientsMap'], clientsMap));
    });
}

export function userRolesMapKey(partnerId, userId) {
    return `${partnerId}:${userId}`;
}

export async function loadUsersRoles(users) {
    if (!users?.length) return null;

    const requests = users.map(user => ({
        partnerId: user.partnerId,
        userId: user.id
    }));
    const rolesByUserId = await getUsersRolesBulk(requests);
    if (!rolesByUserId) return null;

    // Keys are partnerId:userId (see BulkUserRolesResponse.RoleMapKey)
    const rolesMap = new Map(Object.entries(rolesByUserId));
    core.Store().dispatch(core.setScreenCustom(['rolesMap'], rolesMap));
}

let colleaguePiRolesInFlight = null;

export async function loadColleaguePiRoles(partnerId, userId) {
    const cached = core.getModuleCache(core.Store().getState().module, ['matchingPiRoles']);
    if (cached !== undefined && cached !== IDENTITY_ROLES_LOAD_FAILED) {
        return cached;
    }

    if (colleaguePiRolesInFlight) {
        return colleaguePiRolesInFlight;
    }

    colleaguePiRolesInFlight = (async () => {
        const matchingPiRoles = await getColleaguePiRoles(partnerId, userId);

        if (matchingPiRoles == null) {
            core.Store().dispatch(core.setModuleCache(['matchingPiRoles'], IDENTITY_ROLES_LOAD_FAILED));
            return IDENTITY_ROLES_LOAD_FAILED;
        }

        const rolesString = matchingPiRoles.join(',  ');
        core.Store().dispatch(core.setModuleCache(['matchingPiRoles'], rolesString));
        return rolesString;
    })().finally(() => {
        colleaguePiRolesInFlight = null;
    });

    return colleaguePiRolesInFlight;
}

// When refreshing user data, clear existing map for UX clarity
export async function clearUsersRoles() {
    core.Store().dispatch(core.setScreenCustom(['rolesMap'], null));
}

export async function loadUserPermissions() {
    const permissionsResult = await GET(`/user/permissions`);

    if (!permissionsResult.isSuccess) {
        const error = `Failed to load user permissions. ${permissionsResult.error}`;
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false, permissionsResult.errorMessages));
        return false;
    }

    const permissionsMap = {};

    let modules = permissionsResult.response;
    for (let index in modules) {
        if (modules[index]['module_name']) {
            let module = modules[index];
            permissionsMap[module['module_name']] = module;
        }
    }

    core.Store().dispatch(core.setModuleCache(['permissions'], permissionsMap));

    return true;
}

export async function migrateUser() {
    const state = core.Store().getState();

    const update = core.getScreenUpdated(state.screen, [Routes.USER.id]) || new Map();
    const user = core.getScreenData(state.screen, [Routes.USER.id]).toJS();

    if (update.size === 0 || update.get('newPartnerId') == null) {
        core.Store().dispatch(core.setErrorScreenMessage('Must select new partner.', false, [], 'reassignDialog'));
        return;
    }

    core.Store().dispatch(core.toggleMask(true));
    const result = await POST(`/user/${user.partnerId}/users/${user.id}/migrateUser`, update);

    if (!result.isSuccess) {
        const error = 'Error: ' + result.error;
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false, result.errorMessages, 'reassignDialog'));
        core.Store().dispatch(core.toggleMask(false));
        return;
    }

    core.Store().dispatch(core.removeAllScreenMessages('reassignDialog'));
    core.Store().dispatch(core.setSuccessfulScreenMessage('User has successfully been reassigned.', false));
    core.Store().dispatch(core.setScreenData([Routes.USER.id], result.response));
    core.Store().dispatch(core.removeScreenUpdated());
    core.Store().dispatch(core.hideDialog('reassign_user_dialog'));
    core.Store().dispatch(core.toggleMask(false));

    return {
        newPartnerId: update.get('newPartnerId'),
        userId: user.id,
    };
}

export async function editUser() {
    const state = core.Store().getState();
    // return if there are no updates
    if (!core.isDirty(state.screen)) {
        return true;
    }

    let updates = core.getScreenUpdated(state.screen, [Routes.USER.id]) || new Map();
    const user = core.getScreenData(state.screen, [Routes.USER.id]).toJS();
    let isUpdateSuccessful = false;

    if (updates.has('contactInfo')) {
        const contactUpdateResult = await POST(`/user/${user.partnerId}/users/${user.id}/contact`, {
            contactId: updates.get('contactInfo').get('contactId'),
        });

        if (!contactUpdateResult.isSuccess || !contactUpdateResult.response.success) {
            const error = 'Failed to update user. ' + contactUpdateResult.error;
            core.CwLog.error(error);
            core.Store().dispatch(core.setErrorScreenMessage(error, false, contactUpdateResult.errorMessages));
            core.Store().dispatch(core.toggleMask(false));
            return false;
        }

        isUpdateSuccessful = true;
        updates = updates.delete('contactInfo');
    }

    // Handle isDisabled separately via dedicated enable/disable endpoints
    if (updates.has('isDisabled')) {
        const isDisabled = updates.get('isDisabled');
        core.Store().dispatch(core.toggleMask(true));

        const toggleAccountResult = isDisabled
            ? await POST(`/user/${user.partnerId}/users/disable`, [user.id])
            : await POST(`/user/${user.partnerId}/users/enable`, [user.id]);

        if (!toggleAccountResult?.isSuccess) {
            const error = `Failed to ${isDisabled ? 'disable' : 'enable'} user. ` + toggleAccountResult.error;
            core.CwLog.error(error);
            core.Store().dispatch(core.setErrorScreenMessage(error, false, toggleAccountResult.errorMessages));
            core.Store().dispatch(core.toggleMask(false));
            return false;
        }

        isUpdateSuccessful = true;
        updates = updates.delete('isDisabled');
    }

    // Skip PATCH if there are no actual user field updates
    if (!updates || !updates.size || updates.size === 0) {
        if (isUpdateSuccessful) {
            core.Store().dispatch(core.setSuccessfulScreenMessage('Successfully updated user.'));
        }
        core.Store().dispatch(core.toggleMask(false));
        return true;
    }

    core.Store().dispatch(core.toggleMask(true));
    const result = await PATCH(`/user/${user.partnerId}/users/${user.id}`, updates);

    if (!result.isSuccess) {
        const error = 'Failed to update user. ' + result.error;
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false, result.errorMessages));
        core.Store().dispatch(core.toggleMask(false));
        return false;
    }

    core.Store().dispatch(core.setSuccessfulScreenMessage('Successfully updated user.'));
    core.Store().dispatch(core.toggleMask(false));
    return true;
}

export async function editUserRoles() {
    const state = core.Store().getState();
    const user = core.getScreenData(state.screen, [Routes.USER.id]).toJS();
    const updatedAppRoles = core.getScreenCustom(state.screen, ['updatedAppRoles'])?.toJS();
    if (!updatedAppRoles) return true;

    core.Store().dispatch(core.toggleMask(true));

    const newRoleMapping = {
        connectedAppRoles: updatedAppRoles,
    };

    const result = await PUT(`/user/${user.partnerId}/users/${user.id}/roles/cwhome_code`, newRoleMapping);
    if (!result.isSuccess) {
        const error = `Failed to update user roles. ${result.error}`;
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false, result.errorMessages));
        core.Store().dispatch(core.toggleMask(false));
        return false;
    }

    core.Store().dispatch(core.setScreenCustom(['updatedAppRoles'], null));
    core.Store().dispatch(core.setSuccessfulScreenMessage('Successfully updated user roles.'));
    core.Store().dispatch(core.toggleMask(false));
    return true;
}

export async function deleteUser() {
    const state = core.Store().getState();

    const user = core.getScreenData(state.screen, [Routes.USER.id]).toJS();

    core.Store().dispatch(core.toggleMask(true));

    const result = await DELETE(`/user/${user.partnerId}/users/${user.id}`);

    if (!result || !result.isSuccess) {
        let msg = '';
        if (result) {
            if (result.status === 403) msg = 'You are not authorized to take this action.';
            else msg = result.error;
        }
        const error = `Failed to delete user. ${msg}`;
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false, result.errorMessages));
    }

    core.Store().dispatch(core.toggleMask(false));

    const currentState = core.Store().getState();
    const navigate = core.getModuleCache(currentState.module, ['navigate']);
    if (navigate) {
        navigate(-1);
    }
}

export async function getPartnerUsersStats(partnerId, userId) {
    const result = await GET(`/user/${partnerId}/users/${userId}/stats`);

    if (!result.isSuccess) {
        const error = `Failed to find partner status. ${result.error}`;
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false, result.errorMessages));
        return;
    }

    core.Store().dispatch(core.setScreenCustom(['partnerStats'], result.response));
    return result.response;
}

export const getSalesforceContactLink = contactId => {
    const getUrl = contactId => {
        const integrations = core.getModuleConfig(core.Store().getState().module, ['integrations']);
        const salesforceUrl = integrations.get('salesforceUrl');
        return `${salesforceUrl}/lightning/r/Contact/${contactId}/view`;
    };

    return getUrl(contactId);
};

export function* confirmDeleteRequest(message) {
    if (!message) return;
    yield put(
        core.showDialog('confirm_dialog', {
            message: message,
            textNo: 'Cancel',
            textYes: 'Confirm',
        })
    );

    const { type } = yield take(['CONFIRM_DIALOG_CONFIRMED', 'CONFIRM_DIALOG_CANCELLED']);

    if (type === 'CONFIRM_DIALOG_CONFIRMED') {
        deleteUser();
    }

    return;
}

export async function getUserById(partnerId, userId) {
    const result = await GET(`/user/${partnerId}/users/${userId}`);

    if (!result.isSuccess) {
        const error = `Failed to load user detail. ${result.error}`;
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false, result.errorMessages));
        return;
    }

    core.Store().dispatch(core.setScreenCustom(['userDetail'], result.response));
    return;
}

export async function loadUserSearchListForAuditPage() {

    const state = core.Store().getState();
    const page = core.getModuleCache(state.module, [Routes.USERS.id, 'pager', 'currentPage']) || 1;
    const PAGE_SIZE = core.getModuleCache(state.module, [Routes.USERS.id, 'pager', 'pageSize']) || 10;

    let result = undefined;
    const search = core.getModuleCache(state.module, [Routes.AUDITLOGS.id, 'searchUser']) || '';
    const searchParam = search.trim();
    if(searchParam.length>0 && searchParam.length <3)
    {
        const error ="Search should be at least 3 characters";
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false));
        core.Store().dispatch(core.setModuleCache([Routes.AUDITLOGS.id, 'searchUser'], null)); // Clear search after complete
        return;
    }
    result = await GET(`/user/users?search=${searchParam}&page=${page}&pageSize=${PAGE_SIZE}`);
    if (!result.isSuccess) {
        const error = 'Failed to load users list. ' + result.error;
        core.CwLog.error(error);
        core.Store().dispatch(core.setErrorScreenMessage(error, false, result.errorMessages));
        core.Store().dispatch(core.setModuleCache([Routes.USERS.id, 'search'], null)); // Clear search after complete
        return;
    }
    let users = Array.isArray(result.response) ? result.response : [result.response];
   
    // If search term, users list should only be one user total
    // let numberOfPages = page;
    // if (result.headers) {
    //     const parsed = parse(result.headers.get('link'));
    //     if (parsed) {
    //         if (parsed['last'] && parsed['last']['page']) {
    //             numberOfPages = parseInt(parsed['last']['page']);
    //         }
    //     }
    // }
    core.Store().dispatch(core.setScreenData([Routes.AUDITLOGS.id, 'searchUserResult'], users));
}

export function getUserAppRoles() {
        const state = core.Store().getState();
        const userStoreObject = core.getUser(state.oidc);
        const userAppRoles = userStoreObject.getIn(['profile', 'app_role_assignments']);
       
        if (!userAppRoles) 
        {
            return [];
        }
        
        let  userAppRolesParsed = typeof userAppRoles === 'string' || userAppRoles instanceof String ? [userAppRoles] : userAppRoles.toJS();       

        userAppRolesParsed = Array.isArray(userAppRolesParsed)
                ? userAppRolesParsed
                : [userAppRolesParsed];

        return userAppRolesParsed;
}
